import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PriceSiteLayout from "./PriceSiteLayout";

/**
 * Pages du flow de vérification email pour Price :
 *
 *   /verify-email?token=xxx  → handler : appelle l'API et redirige vers
 *                              /email-verifie, /email-deja-verifie,
 *                              /verification-expiree ou /verification-erreur.
 *   /email-verifie           → succès.
 *   /email-deja-verifie      → l'email était déjà vérifié.
 *   /verification-expiree    → lien expiré (>24 h) — bouton "Recevoir un
 *                              nouveau lien" (formulaire email).
 *   /verification-erreur     → lien invalide ou déjà utilisé.
 *
 * Toutes les pages partagent le même layout PriceSite (header/footer)
 * et le même style "cartoon/street" que le site pricecovoit.fr.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Set global des tokens déjà consommés par le handler dans cet onglet —
// nécessaire car React 18 StrictMode remonte le composant en dev et
// réinitialise `useRef`, ce qui provoquerait un double appel à un
// endpoint pourtant à usage unique.
const PROCESSED_TOKENS = new Set();

/* ------------------------------------------------------------------ */
/* Bloc central partagé — hero réutilisé sur les 4 pages               */
/* ------------------------------------------------------------------ */
function VerifyHero({ badge, badgeTone = "primary", title, description, children, testid }) {
  const badgeStyle =
    badgeTone === "primary"
      ? { background: "#F86A78", color: "#FFFFFF" }
      : badgeTone === "warning"
      ? { background: "#F4A825", color: "#1E0910" }
      : badgeTone === "danger"
      ? { background: "#1E0910", color: "#F86A78" }
      : { background: "#1E0910", color: "#FFF6EE" };
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg,#FFF6EE 0%,#FFEEDF 100%)" }}
    >
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(248,106,120,0.20)" }} aria-hidden />
      <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(244,168,37,0.30)" }} aria-hidden />

      <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 py-20 text-center md:py-28" data-testid={testid}>
        <div
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-black uppercase tracking-widest"
          style={{ ...badgeStyle, border: "2px solid #1E0910", boxShadow: "3px 3px 0 0 #1E0910" }}
        >
          {badge}
        </div>
        <h1
          className="mt-8 font-[Outfit] text-[38px] font-black leading-[1.02] tracking-tight sm:text-[48px] lg:text-[56px]"
          style={{ color: "#1E0910" }}
        >
          {title}
        </h1>
        <p
          className="mt-5 max-w-xl font-[Outfit] text-[16px] leading-relaxed sm:text-[17px]"
          style={{ color: "rgba(30,9,16,0.72)" }}
        >
          {description}
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">{children}</div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Boutons partagés                                                    */
/* ------------------------------------------------------------------ */
function OpenPriceButton({ testid = "open-price-btn" }) {
  // Simple lien HTML plein rechargement — évite le custom URL scheme
  // "price://" qui provoquait une erreur "adresse invalide" sur Safari iOS
  // et permet à App.js de whitelister l'host marketing avant de router.
  return (
    <a
      href="/welcome"
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-2xl px-7 py-4 font-[Outfit] text-[15px] font-black tracking-tight transition-transform hover:-translate-y-0.5"
      style={{
        background: "#F86A78",
        color: "#FFFFFF",
        border: "3px solid #1E0910",
        boxShadow: "5px 5px 0 0 #1E0910",
      }}
    >
      Ouvrir la version web
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </a>
  );
}

function OutlineLinkButton({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3.5 font-[Outfit] text-[14px] font-black tracking-tight transition-transform hover:-translate-y-0.5"
      style={{ color: "#1E0910", border: "3px solid #1E0910", boxShadow: "4px 4px 0 0 #1E0910" }}
    >
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Handler /verify-email?token=xxx                                     */
/* ------------------------------------------------------------------ */
export function PriceVerifyEmailHandler() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | done

  useEffect(() => {
    const token = params.get("token") || "";
    if (!token) {
      nav("/verification-erreur", { replace: true });
      return;
    }
    // Empêche le double-fire causé par React 18 StrictMode (dev only).
    // En prod, StrictMode ne monte le composant qu'une seule fois donc
    // le Set reste vide et le premier fetch part immédiatement.
    if (PROCESSED_TOKENS.has(token)) return;
    PROCESSED_TOKENS.add(token);
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
          { method: "GET" }
        );
        const data = await res.json().catch(() => ({}));
        const status = String(data?.status || "").toLowerCase();
        if (status === "verified") nav("/email-verifie", { replace: true });
        else if (status === "already") nav("/email-deja-verifie", { replace: true });
        else if (status === "expired") nav("/verification-expiree", { replace: true });
        else nav("/verification-erreur", { replace: true });
      } catch (_e) {
        nav("/verification-erreur", { replace: true });
      } finally {
        setPhase("done");
      }
    })();
  }, [params, nav]);

  return (
    <PriceSiteLayout>
      <VerifyHero
        badge="Vérification en cours…"
        badgeTone="dark"
        title="Un instant…"
        description="On vérifie ton lien, ça ne prendra qu'une seconde."
        testid="verify-email-loading"
      >
        <div className="mt-2 h-2 w-40 overflow-hidden rounded-full" style={{ background: "rgba(30,9,16,0.1)" }}>
          <div className="h-full w-1/2 animate-pulse rounded-full" style={{ background: "#F86A78" }} />
        </div>
      </VerifyHero>
    </PriceSiteLayout>
  );
}

/* ------------------------------------------------------------------ */
/* /email-verifie — succès                                             */
/* ------------------------------------------------------------------ */
export function PriceEmailVerifiedPage() {
  return (
    <PriceSiteLayout>
      <VerifyHero
        badge="Email confirmé"
        badgeTone="primary"
        title={<>🎉 Adresse email <span style={{ color: "#F86A78" }}>confirmée</span></>}
        description="Merci ! Ton adresse email a bien été vérifiée. Tu peux maintenant utiliser toutes les fonctionnalités de Price."
        testid="verify-email-success"
      >
        <p
          className="max-w-md text-center font-[Outfit] text-[14px] font-semibold leading-relaxed"
          style={{ color: "rgba(30,9,16,0.85)" }}
          data-testid="verify-email-kyc-notice"
        >
          On va te contacter prochainement pour une vérification de document.
        </p>
        <OpenPriceButton />
      </VerifyHero>
    </PriceSiteLayout>
  );
}

/* ------------------------------------------------------------------ */
/* /email-deja-verifie — déjà vérifiée                                 */
/* ------------------------------------------------------------------ */
export function PriceEmailAlreadyVerifiedPage() {
  return (
    <PriceSiteLayout>
      <VerifyHero
        badge="Déjà vérifiée"
        badgeTone="primary"
        title={<>✅ Ton adresse email <span style={{ color: "#F86A78" }}>est déjà vérifiée</span></>}
        description="Pas besoin de recommencer — tu peux directement ouvrir l'application Price."
        testid="verify-email-already"
      >
        <p
          className="max-w-md text-center font-[Outfit] text-[14px] font-semibold leading-relaxed"
          style={{ color: "rgba(30,9,16,0.85)" }}
          data-testid="verify-email-kyc-notice"
        >
          On va te contacter prochainement pour une vérification de document.
        </p>
        <OpenPriceButton />
      </VerifyHero>
    </PriceSiteLayout>
  );
}

/* ------------------------------------------------------------------ */
/* /verification-expiree — lien expiré (avec formulaire resend)        */
/* ------------------------------------------------------------------ */
export function PriceEmailExpiredPage() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const handleResend = async (e) => {
    e.preventDefault();
    if (sending) return;
    setErr("");
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setErr("Adresse email invalide");
      return;
    }
    setSending(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean }),
      });
      setSent(true);
    } catch (_e) {
      setErr("Impossible de contacter le serveur, réessaie dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PriceSiteLayout>
      <VerifyHero
        badge="Lien expiré"
        badgeTone="warning"
        title={<>Ce lien a <span style={{ color: "#F86A78" }}>expiré</span></>}
        description="Pour des raisons de sécurité, ce lien n'est plus valide. Reçois-en un nouveau en quelques secondes."
        testid="verify-email-expired"
      >
        {sent ? (
          <div
            className="max-w-md rounded-2xl bg-white px-6 py-5 text-center"
            style={{ border: "3px solid #1E0910", boxShadow: "4px 4px 0 0 #1E0910" }}
            data-testid="resend-sent"
          >
            <div className="font-[Outfit] text-[15px] font-black" style={{ color: "#1E0910" }}>
              Nouveau lien envoyé
            </div>
            <div className="mt-1 text-[13px] text-neutral-600">
              Si un compte existe pour <strong>{email}</strong>, tu recevras un email dans quelques minutes.
              Pense à vérifier ton dossier spam.
            </div>
          </div>
        ) : (
          <form onSubmit={handleResend} className="w-full max-w-md space-y-3">
            <label className="block text-left text-[10px] font-black uppercase tracking-widest" style={{ color: "rgba(30,9,16,0.6)" }}>
              Ton adresse email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@email.com"
              autoComplete="email"
              inputMode="email"
              required
              data-testid="resend-email-input"
              className="w-full rounded-2xl bg-white px-4 py-3.5 font-[Outfit] font-medium outline-none focus:ring-4"
              style={{
                border: "3px solid #1E0910",
                boxShadow: "3px 3px 0 0 #1E0910",
                color: "#1E0910",
                fontSize: "16px",
              }}
            />
            {err && (
              <div className="text-left text-[13px] font-bold" style={{ color: "#B00020" }} data-testid="resend-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={sending}
              data-testid="resend-submit-btn"
              className="w-full rounded-2xl px-6 py-4 font-[Outfit] text-[15px] font-black tracking-tight transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{
                background: "#F86A78",
                color: "#FFFFFF",
                border: "3px solid #1E0910",
                boxShadow: "5px 5px 0 0 #1E0910",
              }}
            >
              {sending ? "Envoi en cours…" : "Recevoir un nouveau lien"}
            </button>
          </form>
        )}
      </VerifyHero>
    </PriceSiteLayout>
  );
}

/* ------------------------------------------------------------------ */
/* /verification-erreur — invalide ou déjà utilisé                     */
/* ------------------------------------------------------------------ */
export function PriceEmailErrorPage() {
  return (
    <PriceSiteLayout>
      <VerifyHero
        badge="Lien non valide"
        badgeTone="danger"
        title={<>Impossible de vérifier <span style={{ color: "#F86A78" }}>cette adresse</span></>}
        description="Le lien est invalide ou a déjà été utilisé. Retourne à la connexion pour continuer."
        testid="verify-email-error"
      >
        <OutlineLinkButton to="/login" testid="verify-error-login-btn">
          Retour à la connexion
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </OutlineLinkButton>
      </VerifyHero>
    </PriceSiteLayout>
  );
}
